import { useState } from "react";
import { useChat } from "../zustand/ChatZustand";
import { Warnung } from "./Symbole";

/*
  Ein neues Gerät freischalten.

  Warum es das überhaupt braucht: Bei Ende-zu-Ende-Verschlüsselung liegen die
  Schlüssel auf den Geräten, nicht auf dem Server. Ein Gerät, das gerade erst
  dazugekommen ist, war bei den alten Beiträgen nicht dabei und kann sie
  deshalb nicht lesen. Kein Fehler, sondern der Zweck der Sache.

  Jeder verschlüsselte Messenger löst das mit einer einmaligen Frage je
  Gerät. WhatsApp Web lässt einen QR-Code scannen, Signal fragt nach der PIN.
  Hier sind es zwei Wege:

    1. Am anderen Gerät bestätigen — der Alltagsweg. Auf beiden Geräten
       erscheinen dieselben sieben Bilder. Stimmen sie überein, steht
       niemand dazwischen.
    2. Wiederherstellungsschlüssel eintippen — die Rückfalltür, wenn gerade
       kein zweites Gerät zur Hand ist.

  Das Fenster ist wegklickbar. Wer nur schnell etwas Neues schreiben will,
  soll nicht blockiert sein — das geht auch ohne Freischaltung.
*/

export default function Geraetefreischaltung() {
  const chat = useChat();
  const [offen, setzeOffen] = useState(true);

  if (!chat.freischaltungNoetig || !offen) return null;

  return (
    <Freischaltfenster
      aufSchliessen={() => setzeOffen(false)}
      aufFertig={chat.freigeschaltet}
      chat={chat}
    />
  );
}

function Freischaltfenster({ aufSchliessen, aufFertig, chat }) {
  const [weg, setzeWeg] = useState("bestaetigen");
  const [bilder, setzeBilder] = useState(null);
  const [laeuft, setzeLaeuft] = useState(false);
  const [meldung, setzeMeldung] = useState(null);
  const [fehler, setzeFehler] = useState(null);
  const [fortschritt, setzeFortschritt] = useState(null);
  const [schluessel, setzeSchluessel] = useState("");

  async function holeVerlauf() {
    setzeBilder(null);
    setzeMeldung("Der Verlauf wird geholt …");
    await aufFertig((fertig, gesamt) => {
      setzeFortschritt({ fertig, gesamt });
    });
    setzeMeldung(null);
    aufSchliessen();
  }

  async function bestaetigenStarten() {
    setzeFehler(null);
    setzeLaeuft(true);
    setzeMeldung("Auf dem anderen Gerät erscheint jetzt eine Frage …");

    try {
      await chat.bestaetigungAnfordern({
        aufBilder: (angaben) => {
          setzeMeldung(null);
          setzeBilder(angaben);
        },
        aufFertig: () => {
          setzeLaeuft(false);
          holeVerlauf();
        },
        aufAbbruch: (text) => {
          setzeLaeuft(false);
          setzeBilder(null);
          setzeMeldung(null);
          setzeFehler(text);
        },
      });
    } catch (f) {
      setzeLaeuft(false);
      setzeMeldung(null);
      setzeFehler(f.message || "Die Bestätigung hat nicht geklappt.");
    }
  }

  async function mitSchluessel() {
    setzeFehler(null);
    setzeLaeuft(true);
    try {
      await chat.mitSchluesselFreischalten(schluessel);
      setzeLaeuft(false);
      await holeVerlauf();
    } catch (f) {
      setzeLaeuft(false);
      setzeFehler(f.message);
    }
  }

  return (
    <div className="ueberlagerung" role="dialog" aria-label="Gerät freischalten">
      <div className="ueberlagerung__karte">
        <p style={{ fontWeight: 700, fontSize: "var(--schrift-gross)" }}>
          Dieses Gerät ist neu
        </p>
        <p className="klein leise" style={{ marginTop: "var(--luft-2)" }}>
          Neue Beiträge kannst du sofort schreiben und lesen. Für den{" "}
          <strong>bisherigen Verlauf</strong> muss dieses Gerät einmal freigeschaltet werden —
          danach nie wieder.
        </p>

        {bilder ? (
          <Bildvergleich
            bilder={bilder}
            aufFehler={(t) => {
              setzeBilder(null);
              setzeFehler(t);
            }}
          />
        ) : (
          <>
            <div className="wahl wahl--eng" style={{ marginTop: "var(--luft-4)" }}>
              {[
                ["bestaetigen", "Am anderen Gerät bestätigen"],
                ["schluessel", "Schlüssel eingeben"],
              ].map(([wert, beschriftung]) => (
                <button
                  key={wert}
                  className="wahl__knopf"
                  aria-pressed={weg === wert}
                  /*
                    Wechseln bleibt möglich, auch während auf das andere
                    Gerät gewartet wird. Wer merkt, dass der Rechner aus ist,
                    säße sonst fest.
                  */
                  onClick={() => {
                    setzeWeg(wert);
                    setzeFehler(null);
                    setzeLaeuft(false);
                    setzeMeldung(null);
                  }}
                >
                  {beschriftung}
                </button>
              ))}
            </div>

            {weg === "bestaetigen" ? (
              <>
                <p className="klein leise" style={{ marginTop: "var(--luft-4)" }}>
                  Nimm ein Gerät, auf dem das Fraktionsbüro schon läuft — den Rechner oder
                  das Handy. Dort erscheint gleich dieselbe Frage. Auf beiden stehen dann
                  sieben Bilder; wenn sie übereinstimmen, ist alles in Ordnung.
                </p>
                <button
                  className="knopf knopf--breit"
                  style={{ marginTop: "var(--luft-4)" }}
                  onClick={bestaetigenStarten}
                  disabled={laeuft}
                >
                  {laeuft ? "Warte auf das andere Gerät …" : "Anfrage senden"}
                </button>
              </>
            ) : (
              <>
                <p className="klein leise" style={{ marginTop: "var(--luft-4)" }}>
                  Der Wiederherstellungsschlüssel wurde dir beim ersten Anmelden einmal
                  angezeigt — zwölf Vierergruppen. Es ist nicht dein Passwort.
                </p>
                <textarea
                  className="anmerkfeld"
                  style={{ marginTop: "var(--luft-3)", minHeight: "4.5rem" }}
                  placeholder="EsTn e2RB ZDRF M4NX …"
                  value={schluessel}
                  onChange={(e) => setzeSchluessel(e.target.value)}
                />
                <button
                  className="knopf knopf--breit"
                  onClick={mitSchluessel}
                  disabled={laeuft || !schluessel.trim()}
                >
                  {laeuft ? "Einen Moment …" : "Freischalten"}
                </button>
              </>
            )}
          </>
        )}

        {meldung && (
          <p className="klein leise" style={{ marginTop: "var(--luft-3)" }}>
            {meldung}
            {fortschritt && fortschritt.gesamt > 0 && (
              <> {fortschritt.fertig} von {fortschritt.gesamt}</>
            )}
          </p>
        )}

        {fehler && (
          <p className="klein" style={{ color: "var(--ampel-nein)", marginTop: "var(--luft-3)" }}>
            {fehler}
          </p>
        )}

        <button
          className="knopf knopf--schlicht knopf--breit"
          style={{ marginTop: "var(--luft-3)" }}
          onClick={aufSchliessen}
        >
          Später
        </button>
      </div>
    </div>
  );
}

/*
  Die sieben Bilder.

  Sie werden aus den Schlüsseln beider Geräte gerechnet. Stimmen sie überein,
  hat niemand die Verbindung übernommen — dafür müsste ein Angreifer beide
  Seiten gleichzeitig täuschen, und dann kämen andere Bilder heraus.

  Deshalb steht hier auch nicht nur „bestätigen“, sondern die ehrliche Frage:
  Siehst du dieselben Bilder? Wer blind tippt, hebt den Schutz auf.
*/
function Bildvergleich({ bilder, aufFehler }) {
  const [laeuft, setzeLaeuft] = useState(false);

  return (
    <div style={{ marginTop: "var(--luft-4)" }}>
      <p style={{ fontWeight: 600 }}>Stehen auf beiden Geräten dieselben Bilder?</p>

      <div className="bilder">
        {(bilder.bilder || []).map(([zeichen, name], i) => (
          <span key={i} className="bilder__eins">
            <span className="bilder__zeichen" aria-hidden="true">
              {zeichen}
            </span>
            <span className="bilder__name">{name}</span>
          </span>
        ))}
      </div>

      <div className="leiste__knoepfe" style={{ marginTop: "var(--luft-4)" }}>
        <button
          className="knopf"
          disabled={laeuft}
          onClick={async () => {
            setzeLaeuft(true);
            await bilder.stimmtUeberein();
          }}
        >
          Ja, stimmt überein
        </button>
        <button
          className="knopf knopf--zurueckhaltend"
          disabled={laeuft}
          onClick={() => {
            bilder.stimmtNicht();
            aufFehler(
              "Abgebrochen. Wenn die Bilder nicht übereinstimmen, versucht " +
                "jemand mitzulesen — sag uns bitte Bescheid."
            );
          }}
        >
          Nein
        </button>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------
   Das Gegenstück: Auf diesem Gerät fragt ein anderes an
   --------------------------------------------------------------- */

/*
  Ohne dieses Fenster liefe die Bestätigung ins Leere — das neue Gerät
  fragte, und niemand antwortete.

  Es steht dabei, welches Gerät fragt. Wer eine Anfrage bekommt, ohne selbst
  gerade ein Gerät anzumelden, soll ablehnen: Dann versucht jemand anderes,
  sich Zugang zu verschaffen.
*/
export function Bestaetigungsanfrage() {
  const chat = useChat();
  const [bilder, setzeBilder] = useState(null);
  const [fehler, setzeFehler] = useState(null);
  const [laeuft, setzeLaeuft] = useState(false);

  const anfrage = chat.anfrage;
  if (!anfrage) return null;

  return (
    <div className="ueberlagerung" role="dialog" aria-label="Anfrage eines anderen Geräts">
      <div className="ueberlagerung__karte">
        {!bilder ? (
          <>
            <div className="ueberlagerung__warnung">
              <p style={{ display: "flex", gap: "var(--luft-3)", alignItems: "flex-start" }}>
                <Warnung groesse={20} />
                <span>
                  <strong>Ein anderes Gerät möchte freigeschaltet werden.</strong> Wenn du
                  dich gerade nirgendwo angemeldet hast, lehne ab — dann versucht jemand
                  anderes, an deine Beiträge zu kommen.
                </span>
              </p>
            </div>

            <p className="klein leise" style={{ marginTop: "var(--luft-3)" }}>
              Gerät: {anfrage.geraet}
            </p>

            <div className="leiste__knoepfe" style={{ marginTop: "var(--luft-4)" }}>
              <button
                className="knopf"
                disabled={laeuft}
                onClick={async () => {
                  setzeLaeuft(true);
                  await anfrage.annehmen({
                    aufBilder: setzeBilder,
                    aufFertig: () => chat.anfrageErledigt(),
                    aufAbbruch: (t) => {
                      setzeFehler(t);
                      setzeLaeuft(false);
                    },
                  });
                }}
              >
                Das bin ich
              </button>
              <button
                className="knopf knopf--zurueckhaltend"
                onClick={() => {
                  anfrage.ablehnen();
                  chat.anfrageErledigt();
                }}
              >
                Ablehnen
              </button>
            </div>
          </>
        ) : (
          <Bildvergleich
            bilder={bilder}
            aufFehler={(t) => {
              setzeBilder(null);
              setzeFehler(t);
            }}
          />
        )}

        {fehler && (
          <p className="klein" style={{ color: "var(--ampel-nein)", marginTop: "var(--luft-3)" }}>
            {fehler}
            <button
              className="knopf knopf--schlicht knopf--breit"
              style={{ marginTop: "var(--luft-3)" }}
              onClick={chat.anfrageErledigt}
            >
              Schließen
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
